/**
 * Firebase utility functions for serialization
 */

/**
 * Serialize data for Firebase storage
 * Handles undefined values and nested objects
 */
export const serializeForFirebase = (obj: any, depth = 0, path = ''): any => {
  // Prevent infinite recursion
  if (depth > 10) {
    console.warn(`Deep nesting detected at path: ${path}`)
    return obj
  }
  
  if (obj === undefined) {
    return null
  }
  
  if (obj === null) {
    return null
  }
  
  // Handle Date objects
  if (obj instanceof Date) {
    return obj
  }
  
  // Handle File objects (shouldn't be serialized)
  if (typeof File !== 'undefined' && obj instanceof File) {
    console.warn(`Attempted to serialize File object at ${path}, returning null`)
    return null
  }
  
  // Handle Blobs (shouldn't be serialized)
  if (typeof Blob !== 'undefined' && obj instanceof Blob) {
    console.warn(`Attempted to serialize Blob object at ${path}, returning null`)
    return null
  }
  
  if (Array.isArray(obj)) {
    // Check if this is a nested array
    const hasNestedArrays = obj.some(item => Array.isArray(item))
    
    if (hasNestedArrays) {
      // Check if it's a coordinates array [[lng, lat], [lng, lat]]
      if (obj.length > 0 && Array.isArray(obj[0]) && obj[0].length === 2 && typeof obj[0][0] === 'number') {
        // This is a coordinate array
        return obj.map((coord, index) => ({ 
          _type: 'coordinate',
          index, 
          lng: coord[0], 
          lat: coord[1] 
        }))
      }
      
      // Check if it's an array of arrays of coordinates (multi-polygon/unpaved segments)
      if (obj.length > 0 && Array.isArray(obj[0]) && obj[0].length > 0 && Array.isArray(obj[0][0]) && obj[0][0].length === 2 && typeof obj[0][0][0] === 'number') {
        return obj.map((polygon, polyIndex) => ({
          _type: 'polygon',
          index: polyIndex,
          coordinates: polygon.map((coord, coordIndex) => ({
            index: coordIndex,
            lng: coord[0],
            lat: coord[1]
          }))
        }))
      }
      
      // For other nested arrays, return empty array
      console.warn(`Unsupported nested array structure at ${path}`)
      return []
    }
    
    return obj.map((item, index) => serializeForFirebase(item, depth + 1, `${path}[${index}]`))
  }
  
  if (typeof obj === 'object') {
    const serialized: any = {}
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        serialized[key] = serializeForFirebase(value, depth + 1, path ? `${path}.${key}` : key)
      }
    }
    return serialized
  }
  
  return obj
}

/**
 * Deserialize data from Firebase storage
 * Handles null values that were originally undefined
 */
export const deserializeFromFirebase = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj
  }
  
  // Handle Firestore Timestamp objects
  if (obj && typeof obj === 'object' && typeof obj.toDate === 'function') {
    return obj.toDate()
  }
  
  // Handle objects with seconds/nanoseconds (Firestore Timestamp plain object)
  if (obj && typeof obj === 'object' && 'seconds' in obj && 'nanoseconds' in obj && Object.keys(obj).length === 2) {
    return new Date(obj.seconds * 1000)
  }
  
  // Handle objects with _seconds/_nanoseconds (some Firestore versions)
  if (obj && typeof obj === 'object' && '_seconds' in obj && '_nanoseconds' in obj) {
    return new Date(obj._seconds * 1000)
  }
  
  if (Array.isArray(obj)) {
    // Check if this is a serialized coordinates array
    if (obj.length > 0 && obj[0] && obj[0]._type === 'coordinate') {
      return obj
        .filter(item => item && item._type === 'coordinate')
        .sort((a, b) => a.index - b.index)
        .map(coord => [coord.lng, coord.lat])
    }
    
    // Check if this is a serialized polygon array
    if (obj.length > 0 && obj[0] && obj[0]._type === 'polygon') {
      return obj
        .sort((a, b) => a.index - b.index)
        .map(polygon => 
          polygon.coordinates
            .sort((a: any, b: any) => a.index - b.index)
            .map((coord: any) => [coord.lng, coord.lat])
        )
    }
    
    return obj.map(item => deserializeFromFirebase(item))
  }
  
  if (typeof obj === 'object') {
    // Check if this is a single coordinate object
    if (obj._type === 'coordinate' && 'lng' in obj && 'lat' in obj) {
      return [obj.lng, obj.lat]
    }
    
    // Check if this is a polygon object (for unpaved segments)
    if (obj._type === 'polygon' && obj.coordinates && Array.isArray(obj.coordinates)) {
      // Return just the coordinates array, properly deserialized
      return obj.coordinates
        .sort((a: any, b: any) => (a.index || 0) - (b.index || 0))
        .map((coord: any) => [coord.lng, coord.lat])
    }
    
    const deserialized: any = {}
    for (const [key, value] of Object.entries(obj)) {
      deserialized[key] = deserializeFromFirebase(value)
    }
    return deserialized
  }
  
  return obj
}